import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { AddVoucherMutationParams, Voucher, createAddVoucherMutation } from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type AddVoucherFormValue = {
	currency: string | null;
	count: string;
	deposit: string;
	wagering: string;
};

type CreateAddVoucherModelParams = {
	$currencies: Store<string[]>;
};

export type AddVoucherModel = ModalFormModel<null, AddVoucherFormValue> & {
	addVoucherMutation: Mutation<AddVoucherMutationParams, Voucher, JsonApiRequestError>;
	$pending: Store<boolean>;
	$currencies: Store<string[]>;
};

export const createAddVoucherModel = ({
	$currencies,
}: CreateAddVoucherModelParams): AddVoucherModel => {
	const addVoucherMutation = createAddVoucherMutation();

	const formModel = createModalFormModel<null, AddVoucherFormValue>({
		fields: {
			currency: {
				init: null,
				rules: [notEmptyRule],
			},
			count: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			deposit: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			wagering: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	sample({
		clock: formModel.form.submitted,
		source: formModel.form.$values,
		fn: (fields) => ({
			currency: fields.currency!,
			count: Number(fields.count),
			deposit: Number(fields.deposit),
			wagering: Number(fields.wagering),
		}),
		target: addVoucherMutation.start,
	});

	notify(addVoucherMutation.finished.success, 'add-voucher.success');

	chain(addVoucherMutation.finished.success, formModel.switcher.disable);

	sample({
		clock: formModel.modalClosed,
		target: [formModel.form.reset, addVoucherMutation.reset],
	});

	return {
		addVoucherMutation,
		$pending: addVoucherMutation.$pending,
		$currencies,
		...formModel,
	};
};
